import { request } from "../../utils/request";

/** GET /video/membership/current 返回体 */
export interface MembershipCurrentRespVO {
  /** 会员记录 ID；非会员为 0/undefined */
  membershipId?: number;
  /** 当前订阅的套餐 ID；非会员为 0/undefined */
  planId?: number;
  planCode?: string;
  planName?: string;
  memberLevel?: number;
  billingCycle?: string;
  /** 会员状态字符串，如 active / expired */
  status?: string;
  startTime?: string;
  endTime?: string;
  /** 下一次会员积分发放时间 */
  nextGrantTime?: string;
  /** 当前可用积分（= memberAvailablePoint + permanentAvailablePoint） */
  availablePoint?: number;
  /** 会员积分（本周期清零） */
  memberAvailablePoint?: number;
  /** 积分包余额（永久） */
  permanentAvailablePoint?: number;
  /** 冻结积分（任务进行中暂扣） */
  frozenPoint?: number;
}

export interface MembershipCurrentResponse {
  code: number;
  data?: MembershipCurrentRespVO;
  msg?: string;
}

/** GET /video/membership/current — 查询当前会员和积分余额 */
export const getMembershipCurrentApi = () =>
  request<MembershipCurrentResponse>(`/video/membership/current`, {
    method: "GET",
  });

/* -------------------------------------------------------------------------- */
/* 套餐（plans）                                                                 */
/* -------------------------------------------------------------------------- */

/** 套餐计费周期枚举 */
export type BillingCycle = "free" | "month" | "year" | string;

/** GET /video/membership/plans 返回项 */
export interface MemberPlanRespVO {
  id?: number;
  code?: string;
  name?: string;
  /** 等级：值越大越高级；freePlan=true 时一般不参与排序 */
  level?: number;
  freePlan?: boolean;
  billingCycle?: BillingCycle;
  /** 价格（元） */
  priceYuan?: number;
  /** 每月赠送积分数 */
  grantPoint?: number;
  periodUnit?: string;
  periodCount?: number;
  grantOnDowngrade?: boolean;
  downgradeGrantPoint?: number;
  reminderDays?: number;
  /** 1 启用 */
  status?: number;
  sortOrder?: number;
}

export interface MemberPlanListResponse {
  code: number;
  data?: MemberPlanRespVO[];
  msg?: string;
}

/** GET /video/membership/plans — 查询可用会员套餐 */
export const getMembershipPlansApi = () =>
  request<MemberPlanListResponse>(`/video/membership/plans`, {
    method: "GET",
  });

/* -------------------------------------------------------------------------- */
/* 订单（order）                                                                 */
/* -------------------------------------------------------------------------- */

/** POST /video/membership/order 请求体 */
export interface MemberOrderCreateReqVO {
  planId: number;
  /** 支付渠道：wechat / alipay 等 */
  payChannel: string;
}

/** GET /video/membership/order/status & POST /video/membership/order 返回体 */
export interface MemberOrderRespVO {
  id?: number;
  userId?: number;
  orderNo?: string;
  planId?: number;
  planCode?: string;
  planName?: string;
  billingCycle?: BillingCycle;
  /** 订单金额（元） */
  amountYuan?: number;
  /** 开通后每月赠送积分 */
  grantPoint?: number;
  payChannel?: string;
  /** 支付状态字符串，如 pending / paid / closed / failed */
  paymentStatus?: string;
  /** 扫码 URL（前端可二次生成二维码） */
  codeUrl?: string;
  /** 二维码 base64 图片（PNG data URL 或纯 base64） */
  qrCodeImage?: string;
  refundStatus?: string;
  refundNo?: string;
  tradeNo?: string;
  refundTradeNo?: string;
  /** 失败原因（paymentStatus=failed 时使用） */
  failReason?: string;
  paymentTime?: string;
  refundTime?: string;
  createTime?: string;
}

export interface MemberOrderResponse {
  code: number;
  data?: MemberOrderRespVO;
  msg?: string;
}

/** POST /video/membership/order — 创建会员支付订单 */
export const createMembershipOrderApi = (req: MemberOrderCreateReqVO) =>
  request<MemberOrderResponse>(`/video/membership/order`, {
    method: "POST",
    body: req,
  });

/** GET /video/membership/order/status?orderNo=xxx — 查询会员订单支付状态（轮询用） */
export const getMembershipOrderStatusApi = (orderNo: string) =>
  request<MemberOrderResponse>(`/video/membership/order/status`, {
    method: "GET",
    params: { orderNo },
  });

/* -------------------------------------------------------------------------- */
/* 订单列表 & 退款                                                                 */
/* -------------------------------------------------------------------------- */

export interface MemberOrderListResponse {
  code: number;
  data?: MemberOrderRespVO[];
  msg?: string;
}

/** GET /video/membership/orders — 查询当前用户会员订单 */
export const getMembershipOrdersApi = () =>
  request<MemberOrderListResponse>(`/video/membership/orders`, {
    method: "GET",
  });

/** POST /video/membership/refund?orderId=xxx — 全额退款当前生效会员订单 */
export const refundMembershipOrderApi = (orderId: number) =>
  request<MemberOrderResponse>(`/video/membership/refund`, {
    method: "POST",
    params: { orderId },
  });

/* -------------------------------------------------------------------------- */
/* 全局刷新事件                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * 通知全局监听器重新拉取 /video/membership/current 并写回 globalStore。
 * 业务侧在支付成功、积分消耗等可能改变会员/积分快照的动作后调用；
 * 与 triggerPointsRefresh（service/points）配套使用。
 *
 * 节流窗口：1.5s 内重复触发会被合并，避免路由跳转 + visibilitychange +
 * 业务派发叠加导致 1s 内打 3 次相同请求。窗口内只有第一次真正派发事件。
 */
const MEMBERSHIP_REFRESH_THROTTLE_MS = 1500;
let lastMembershipRefreshTriggeredAt = 0;

export const triggerMembershipRefresh = (): void => {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastMembershipRefreshTriggeredAt < MEMBERSHIP_REFRESH_THROTTLE_MS) {
    return;
  }
  lastMembershipRefreshTriggeredAt = now;
  window.dispatchEvent(new Event("membership:refresh"));
};